import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../../services/api';

export interface EmergingIndicationOption {
  id: string;
  name: string;
}

interface AssetLinksPanelProps {
  assetId: number;
  indicationOptions: EmergingIndicationOption[];
  onError?: (message: string | null) => void;
}

type IndicationStatusRow = {
  indication_code: string;
  indication_name: string;
  lifecycle_status: string | null;
};

type LinkedTrial = {
  id: number;
  nct_id?: string | null;
  trial_name?: string | null;
  phases?: string | null;
  indication_names?: string[];
};

type LinkedFda = {
  message_id: string;
  regimen?: string | null;
  subindication?: string | null;
  linked_indication_name?: string | null;
};

const AssetLinksPanel: React.FC<AssetLinksPanelProps> = ({ assetId, indicationOptions, onError }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [assetName, setAssetName] = useState('');
  const [statuses, setStatuses] = useState<IndicationStatusRow[]>([]);
  const [trials, setTrials] = useState<LinkedTrial[]>([]);
  const [fdaRows, setFdaRows] = useState<LinkedFda[]>([]);
  const [newIndicationId, setNewIndicationId] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      onError?.(null);
      const response = await apiService.fetchEmergingAssetLinks(assetId);
      if (!response?.success) {
        throw new Error(response?.error || 'Failed to load asset links');
      }
      setAssetName(response.asset?.asset_name || '');
      setStatuses(response.indication_statuses || []);
      setTrials(response.trials || []);
      setFdaRows(response.fda_approvals || []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load asset links';
      onError?.(msg);
    } finally {
      setLoading(false);
    }
  }, [assetId, onError]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRemoveIndication = async (indicationCode: string) => {
    if (!window.confirm('Unlink this indication from this asset?')) return;
    try {
      setSaving(true);
      const response = await apiService.unlinkEmergingAsset({
        asset_id: assetId,
        indication_code: indicationCode,
      });
      if (!response?.success) throw new Error(response?.error || 'Failed to remove');
      await load();
    } catch (err: unknown) {
      onError?.(err instanceof Error ? err.message : 'Failed to remove indication');
    } finally {
      setSaving(false);
    }
  };

  const handleAddIndication = async () => {
    if (!newIndicationId) {
      onError?.('Select an indication.');
      return;
    }
    try {
      setSaving(true);
      const response = await apiService.linkEmergingAsset({
        asset_id: assetId,
        indication_code: Number(newIndicationId),
      });
      if (!response?.success) throw new Error(response?.error || 'Failed to add indication');
      setNewIndicationId('');
      await load();
    } catch (err: unknown) {
      onError?.(err instanceof Error ? err.message : 'Failed to add indication');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-xs text-slate-500 py-3">Loading links…</div>;
  }

  return (
    <div className="mt-3 rounded-xl border border-indigo-100 bg-indigo-50/40 p-4 space-y-4 text-xs">
      <div className="flex items-center justify-between gap-2">
        <p className="font-semibold text-slate-800">
          Indication links for <span className="text-indigo-700">{assetName || `#${assetId}`}</span>
        </p>
        <Link to={`/emerging-assets/${assetId}`} className="text-indigo-600 font-semibold hover:underline">
          Open asset page →
        </Link>
      </div>

      <section className="space-y-2">
        <h3 className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
          Linked indications
        </h3>
        {statuses.length === 0 ? (
          <p className="text-slate-500">No indications linked yet.</p>
        ) : (
          <div className="space-y-2">
            {statuses.map((row) => (
              <div key={row.indication_code} className="flex flex-wrap items-center gap-2 bg-white rounded-lg border border-slate-200 px-2 py-2">
                <span className="font-medium text-slate-800 min-w-[140px]">{row.indication_name}</span>
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => handleRemoveIndication(row.indication_code)}
                  className="px-2 py-1 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-60"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-end gap-2 pt-1">
          <label className="space-y-1">
            <span className="text-[10px] font-semibold text-slate-500 uppercase">Add indication</span>
            <select
              value={newIndicationId}
              onChange={(e) => setNewIndicationId(e.target.value)}
              className="block rounded-md border border-slate-200 px-2 py-1 text-xs min-w-[200px]"
            >
              <option value="">Select…</option>
              {indicationOptions.map((ind) => (
                <option key={ind.id} value={ind.id}>
                  {ind.name}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            disabled={saving}
            onClick={handleAddIndication}
            className="px-3 py-1.5 rounded-md bg-slate-800 text-white font-semibold disabled:opacity-60"
          >
            Add
          </button>
        </div>
      </section>

      <section className="space-y-2">
        <h3 className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Linked trials ({trials.length})</h3>
        {trials.length === 0 ? (
          <p className="text-slate-500">No trials linked via asset_ids.</p>
        ) : (
          <ul className="space-y-1 max-h-40 overflow-y-auto">
            {trials.map((t) => (
              <li key={t.id} className="bg-white rounded-md border border-slate-200 px-2 py-1.5">
                <Link to={`/trials/${t.id}`} className="font-semibold text-indigo-700 hover:underline">
                  {t.trial_name || t.nct_id || `Trial #${t.id}`}
                </Link>
                <span className="text-slate-500 ml-2">
                  {t.phases || '—'}
                  {t.indication_names?.length ? ` · ${t.indication_names.join(', ')}` : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
        <Link to="/superuser/ongoing-trials" className="text-indigo-600 font-semibold hover:underline">
          Edit trial links →
        </Link>
      </section>

      <section className="space-y-2">
        <h3 className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
          Linked FDA approvals ({fdaRows.length})
        </h3>
        {fdaRows.length === 0 ? (
          <p className="text-slate-500">No FDA rows linked to this asset yet.</p>
        ) : (
          <ul className="space-y-1 max-h-32 overflow-y-auto">
            {fdaRows.map((f) => (
              <li key={f.message_id} className="bg-white rounded-md border border-slate-200 px-2 py-1.5">
                <span className="font-medium text-slate-800">{f.regimen || f.message_id}</span>
                {f.subindication && <span className="text-slate-500 ml-1">· {f.subindication}</span>}
                {f.linked_indication_name && (
                  <span className="text-emerald-700 ml-1">· {f.linked_indication_name}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default AssetLinksPanel;
